import { Typeahead, type TypeaheadProps } from '@/components/ui/typeahead';
import { useQuickCreate } from '@/features/quick-create/use-quick-create';

/**
 * THE CUSTOMER/SUPPLIER PICKER WITH GEN-51'S ROW ON IT — one component, so
 * the rule is satisfied once rather than at thirteen call sites.
 *
 * Every partner picker in this product already shared its DATA half
 * (`usePartnerPicker`) and its RENDERING half (`<Typeahead>`). What it did not
 * share was the create-new offer, and GEN-51 is a per-FIELD rule — so without
 * something like this the offer would be thirteen independent decisions, and
 * the first picker somebody forgot would be invisible: a supplier field with
 * no create row looks exactly like a supplier field nobody has got to yet.
 *
 * The swap at each site is `<Typeahead` → `<PartnerTypeahead` plus the one
 * word saying which kind of partner it picks. Everything else — the roster
 * lookup, the permission, the window, the focus trip — is behind this.
 *
 * `entity` is required and has no default, deliberately. A default of
 * 'customer' would make a forgotten supplier picker quietly create customers,
 * which is a data defect that reads as a typo and would be found by a
 * confused user rather than by a compiler.
 */
export function PartnerTypeahead({
  entity,
  onCreated,
  ...typeahead
}: TypeaheadProps & {
  entity: 'customer' | 'supplier';
  /**
   * Extra work the screen does when a partner is BORN rather than merely
   * picked — pulling payment terms across, for instance. The field itself is
   * always set; this is in addition to it.
   */
  onCreated?: ((id: string) => void) | undefined;
}) {
  const quick = useQuickCreate(entity, (id) => {
    // The picker takes the new record. This is the only moment the field's
    // value moves as a result of the create row being chosen — choosing the
    // row itself changes nothing, so a cancelled window leaves the field
    // exactly as it found it.
    typeahead.onChange(id);
    onCreated?.(id);
  });

  return (
    <>
      <Typeahead {...typeahead} createNew={quick.createNew} />
      {quick.dialog}
    </>
  );
}
